import { onBeforeUnmount, watch, nextTick, type Ref } from 'vue'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Traps Tab/Shift+Tab focus inside `containerRef` while `isOpen()` is true -
 * needed for modals that must not let keyboard focus escape to the page
 * behind them (e.g. the mandatory Terms & Conditions modal, which has no
 * Escape/click-outside dismissal to fall back on). No other modal in the app
 * needs this today: the rest are dismissable, so losing focus to the page
 * behind them is harmless.
 *
 * On open, focus moves to the container's first focusable element (or the
 * container itself if it has none) so screen readers land inside the dialog
 * immediately.
 */
export function useFocusTrap(containerRef: Ref<HTMLElement | null | undefined>, isOpen: () => boolean): void {
  const getFocusable = (): HTMLElement[] => {
    const container = containerRef.value
    if (!container) return []
    return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.offsetParent !== null,
    )
  }

  const handler = (event: KeyboardEvent) => {
    if (event.key !== 'Tab') return
    const container = containerRef.value
    if (!container) return

    const focusable = getFocusable()
    if (focusable.length === 0) {
      event.preventDefault()
      container.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement as HTMLElement | null

    if (event.shiftKey) {
      if (active === first || !container.contains(active)) {
        event.preventDefault()
        last.focus()
      }
    } else {
      if (active === last || !container.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  watch(
    isOpen,
    async (open) => {
      if (open) {
        document.addEventListener('keydown', handler, true)
        await nextTick()
        const focusable = getFocusable()
        ;(focusable[0] ?? containerRef.value)?.focus()
      } else {
        document.removeEventListener('keydown', handler, true)
      }
    },
    { immediate: true },
  )

  onBeforeUnmount(() => document.removeEventListener('keydown', handler, true))
}
